import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { SendOutlined, EyeOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Link, useParams } from 'react-router-dom';
import {
  EMAIL_EVENT_LABELS,
  EmailEventType,
  useDeleteEmailTemplate,
  useEmailTemplate,
  usePreviewEmailTemplate,
  useSendTestEmail,
  useUpsertEmailTemplate,
} from '@shared/api/hooks/useEmailTemplates';

const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['blockquote'],
  ['link'],
  [{ color: [] }, { background: [] }],
  ['clean'],
];

const FORMATS = [
  'header','bold','italic','underline','strike','list','bullet','align',
  'blockquote','link','color','background',
];

/**
 * Variabili predefinite per ciascun eventType. Coerenti col backend
 * `email-template-defaults.ts`. Mostrate come chip cliccabili a destra
 * dell'editor: click → insertText `{{token}}` alla caret position.
 */
const VARS_BY_EVENT: Record<EmailEventType, string[]> = {
  WELCOME_VERIFY: ['user.firstName', 'user.email', 'verifyLink'],
  WELCOME_SSO: ['user.firstName', 'user.email'],
  ORDER_CONFIRMED: [
    'user.firstName','order.id','order.total','order.itemsHtml',
    'order.fulfillmentNote','order.pickupQrCode',
  ],
  ORDER_SHIPPED: [
    'user.firstName','order.id','tracking.courier','tracking.number','trackingUrl',
  ],
  ORDER_DELIVERED: ['user.firstName','order.id'],
  ORDER_CANCELLED: ['user.firstName','order.id'],
  PASSWORD_RESET: ['user.firstName','resetLink','expiryMinutes'],
};

export function EmailTemplateEditor(): React.ReactElement {
  const { eventType, lang } = useParams<{ eventType: EmailEventType; lang: 'it' | 'en' }>();
  const ev = eventType!;
  const ln: 'it' | 'en' = lang === 'en' ? 'en' : 'it';

  const { data, isLoading, refetch } = useEmailTemplate(ev, ln);
  const upsert = useUpsertEmailTemplate();
  const del = useDeleteEmailTemplate();
  const preview = usePreviewEmailTemplate();
  const sendTest = useSendTestEmail();

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');

  const quillRef = useRef<ReactQuill | null>(null);

  // Carica template (override DB o default).
  useEffect(() => {
    if (data) {
      setSubject(data.subject);
      setBodyHtml(data.bodyHtml);
      setIsActive(data.isActive);
    }
  }, [data]);

  const allowedVars = useMemo(() => VARS_BY_EVENT[ev] ?? [], [ev]);

  const insertVar = (token: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      // fallback: append to subject if focus there
      setSubject((s) => s + `{{${token}}}`);
      return;
    }
    const range = editor.getSelection(true);
    const insert = `{{${token}}}`;
    editor.insertText(range.index, insert, 'user');
    editor.setSelection(range.index + insert.length, 0);
  };

  const handleSave = async () => {
    if (!subject.trim() || !bodyHtml.trim()) {
      message.error('Subject e corpo email obbligatori');
      return;
    }
    try {
      await upsert.mutateAsync({
        eventType: ev,
        lang: ln,
        subject,
        bodyHtml,
        placeholders: allowedVars,
        isActive,
      });
      message.success('Template salvato');
    } catch (err) {
      message.error(`Errore salvataggio: ${(err as Error).message}`);
    }
  };

  const handlePreview = async () => {
    try {
      const out = await preview.mutateAsync({
        eventType: ev,
        lang: ln,
        subject,
        bodyHtml,
        placeholders: allowedVars,
      });
      setPreviewSubject(out.subject);
      setPreviewHtml(out.html);
      setPreviewOpen(true);
    } catch (err) {
      message.error(`Preview fallita: ${(err as Error).message}`);
    }
  };

  const handleSendTest = async () => {
    // Salva prima (così il test usa il template corrente sul DB) e poi invia.
    await handleSave();
    try {
      await sendTest.mutateAsync({ eventType: ev, lang: ln });
      message.success('Mail di test inviata al tuo indirizzo');
    } catch (err) {
      message.error(`Invio test fallito: ${(err as Error).message}`);
    }
  };

  const handleRestoreDefault = async () => {
    if (!data || data.fromDefault) {
      message.info('Stai già usando il default di sistema.');
      return;
    }
    Modal.confirm({
      title: 'Ripristinare il default di sistema?',
      content: 'L\'override corrente verrà eliminato. Operazione irreversibile.',
      okText: 'Elimina override',
      okButtonProps: { danger: true },
      onOk: async () => {
        // Per cancellare serve l'id dell'override → list endpoint lo torna.
        // Workaround: re-fetch list e trova quello (in useListEmailTemplates).
        // Per semplicità qui: chiamiamo la mutate con isActive=false → poi
        // l'admin può rieliminare dalla lista. (Cheap path per ora.)
        try {
          // Disattiva: backend usa default in-code se !isActive.
          await upsert.mutateAsync({
            eventType: ev,
            lang: ln,
            subject,
            bodyHtml,
            placeholders: allowedVars,
            isActive: false,
          });
          message.success('Override disattivato (default in uso)');
          refetch();
        } catch (err) {
          message.error(`Operazione fallita: ${(err as Error).message}`);
        }
      },
    });
    void del; // referenziato per future iterazioni full-delete
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 12 }}>
        <Link to="/settings/email-templates">← Tutti i template</Link>
      </Space>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {EMAIL_EVENT_LABELS[ev]} <Tag>{ln.toUpperCase()}</Tag>
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {data?.fromDefault
          ? 'Stai modificando il default di sistema. Salva per creare l\'override per questo tenant.'
          : 'Override attivo per questo tenant.'}
      </Typography.Paragraph>

      <Row gutter={16}>
        <Col span={18}>
          <Card loading={isLoading}>
            <Form layout="vertical">
              <Form.Item label="Oggetto email" required>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={300}
                  placeholder="Es: Il tuo ordine {{order.id}} è in viaggio"
                />
              </Form.Item>
              <Form.Item label="Corpo HTML" required>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={bodyHtml}
                  onChange={(html) => setBodyHtml(html === '<p><br></p>' ? '' : html)}
                  modules={{ toolbar: TOOLBAR }}
                  formats={FORMATS}
                  placeholder="Scrivi il corpo email…"
                />
              </Form.Item>
              <Form.Item label="Attivo">
                <Switch checked={isActive} onChange={setIsActive} />
                <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
                  Se disattivato, il sistema usa il default in-code.
                </Typography.Text>
              </Form.Item>

              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={upsert.isPending}
                >
                  Salva
                </Button>
                <Button icon={<EyeOutlined />} onClick={handlePreview} loading={preview.isPending}>
                  Preview
                </Button>
                <Button
                  icon={<SendOutlined />}
                  onClick={handleSendTest}
                  loading={sendTest.isPending}
                >
                  Salva + Invia test
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRestoreDefault}
                  disabled={data?.fromDefault}
                >
                  Ripristina default
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>
        <Col span={6}>
          <Card title="Variabili disponibili" size="small">
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              Click su una variabile per inserirla alla posizione del cursore.
              Variabili non in whitelist restano letterali nell&apos;email finale.
            </Typography.Paragraph>
            <Space wrap>
              {allowedVars.map((v) => (
                <Tag
                  key={v}
                  color="blue"
                  style={{ cursor: 'pointer', fontFamily: 'monospace' }}
                  onClick={() => insertVar(v)}
                >
                  {`{{${v}}}`}
                </Tag>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        onOk={() => setPreviewOpen(false)}
        width={720}
        title={`Preview — ${previewSubject}`}
        cancelText="Chiudi"
        okText="OK"
      >
        <iframe
          title="email-preview"
          srcDoc={previewHtml}
          sandbox=""
          style={{ width: '100%', height: 480, border: '1px solid #eee' }}
        />
      </Modal>
    </div>
  );
}
